import React, { useEffect, useState } from 'react';
import Logo from './components/Logo';

function App() {
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    setMessage('');
    try {
      const res = await fetch(`/api/status`);
      const data = await res.json();
      setStatus(data);
    } catch (err) {
      setMessage('Failed to fetch status.');
    } finally {
      setLoading(false);
    }
  };

  const triggerAction = async (endpoint) => {
    setLoading(true);
    setMessage('');
    try {
      const res = await fetch(`/api/${endpoint}`, { method: 'POST' });
      const data = await res.json();
      setMessage(JSON.stringify(data));
    } catch (err) {
      setMessage(`Error contacting /${endpoint}`);
    } finally {
      setLoading(false);
    }
  };

  const formatResponse = (jsonString) => {
    try {
      const data = JSON.parse(jsonString);
      return (
        <div style={{ textAlign: 'left' }}>
          {Object.entries(data).map(([key, value]) => {
            // Handle nested objects
            if (typeof value === 'object' && value !== null) {
              return (
                <div key={key} style={{ marginBottom: '1rem' }}>
                  <h3 style={{ 
                    color: '#3498db',
                    marginBottom: '0.5rem',
                    textTransform: 'capitalize'
                  }}>
                    {key.replace(/_/g, ' ')}
                  </h3>
                  <div style={{ 
                    paddingLeft: '1rem',
                    borderLeft: '3px solid #3498db'
                  }}>
                    {Object.entries(value).map(([subKey, subValue]) => (
                      <div key={subKey} style={{ marginBottom: '0.5rem' }}>
                        <span style={{ 
                          color: '#7f8c8d',
                          textTransform: 'capitalize'
                        }}>
                          {subKey.replace(/_/g, ' ')}:
                        </span>{' '}
                        <span style={{ 
                          color: '#2c3e50',
                          fontWeight: '500'
                        }}>
                          {typeof subValue === 'boolean' 
                            ? (subValue ? 'Yes' : 'No')
                            : subValue}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            // Handle simple key-value pairs
            return (
              <div key={key} style={{ marginBottom: '0.5rem' }}>
                <span style={{ 
                  color: '#7f8c8d',
                  textTransform: 'capitalize'
                }}>
                  {key.replace(/_/g, ' ')}:
                </span>{' '}
                <span style={{ 
                  color: '#2c3e50',
                  fontWeight: '500'
                }}>
                  {typeof value === 'boolean' 
                    ? (value ? 'Yes' : 'No')
                    : value}
                </span>
              </div>
            );
          })}
        </div>
      );
    } catch (e) {
      return <div style={{ color: '#e74c3c' }}>{jsonString}</div>;
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  return (
    <div style={{ 
      fontFamily: 'sans-serif', 
      padding: '2rem',
      maxWidth: '1200px',
      margin: '0 auto',
      backgroundColor: '#f5f5f5',
      minHeight: '100vh'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        marginBottom: '2rem',
        borderBottom: '2px solid #3498db',
        paddingBottom: '1rem'
      }}>
        <Logo size={50} />
        <h1 style={{ 
          color: '#2c3e50',
          margin: 0
        }}>Flight Control Automation System</h1>
      </div>

      <div style={{ 
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '2rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ color: '#2c3e50', marginBottom: '1rem' }}>Status</h2>
          {status && (
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {Object.entries(status).map(([key, value]) => (
                <li key={key} style={{ 
                  padding: '0.5rem 0',
                  borderBottom: '1px solid #eee'
                }}>
                  <strong style={{ color: '#3498db' }}>{key}:</strong> {value}
                </li>
              ))}
            </ul>
          )}
          <button 
            onClick={fetchStatus}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Refresh Status
          </button>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ color: '#2c3e50', marginBottom: '1rem' }}>Actions</h2>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '0.5rem'
          }}>
            <button 
              onClick={() => triggerAction('activate_thrusters')}
              disabled={loading}
              style={{
                padding: '0.75rem',
                backgroundColor: loading ? '#95a5a6' : '#2ecc71',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s',
                opacity: loading ? 0.7 : 1
              }}
            >
              Activate Thrusters
            </button>
            <button 
              onClick={() => triggerAction('close_airlock')}
              disabled={loading}
              style={{
                padding: '0.75rem',
                backgroundColor: loading ? '#95a5a6' : '#e74c3c',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s',
                opacity: loading ? 0.7 : 1
              }}
            >
              Close Airlock
            </button>
            <button 
              onClick={() => triggerAction('reboot_main_computer')}
              disabled={loading}
              style={{
                padding: '0.75rem',
                backgroundColor: loading ? '#95a5a6' : '#e67e22',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s',
                opacity: loading ? 0.7 : 1
              }}
            >
              Reboot Main Computer
            </button>
            <button 
              onClick={() => triggerAction('reroute_power')}
              disabled={loading}
              style={{
                padding: '0.75rem',
                backgroundColor: loading ? '#95a5a6' : '#9b59b6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s',
                opacity: loading ? 0.7 : 1
              }}
            >
              Reroute Power
            </button>
            <button 
              onClick={() => triggerAction('start_reentry')}
              disabled={loading}
              style={{
                padding: '0.75rem',
                backgroundColor: loading ? '#95a5a6' : '#c0392b',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s',
                opacity: loading ? 0.7 : 1
              }}
            >
              Start Reentry Sequence
            </button>
          </div>
        </div>
      </div>

      {(message || loading) && (
        <div style={{ 
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginTop: '1rem'
        }}>
          <h2 style={{ 
            color: '#2c3e50', 
            marginBottom: '1rem',
            borderBottom: '2px solid #3498db',
            paddingBottom: '0.5rem'
          }}>Response</h2>
          {loading ? (
            <div style={{ 
              color: '#3498db',
              fontSize: '1.1rem',
              padding: '1rem 0'
            }}>
              Loading...
            </div>
          ) : (
            formatResponse(message)
          )}
        </div>
      )}
    </div>
  );
}

export default App;
